import type { Action } from '@reduxjs/toolkit';
import { CLARITY_TOKEN_PREFIX } from './types';

/** Defensive Redux DevTools redaction for unexpected token-bearing errors.
 * Only what DevTools records changes; the store itself is untouched.
 */

export const REDACTED = '[redacted]';
const API_PREFIX = 'organizationsApi/';
const MUTATIONS_REDUCER = 'organizationsApi';

function redactArray(value: unknown[]): unknown[] {
  let changed = false;
  const next = value.map((item) => {
    const redacted = redactClarityTokens(item);
    if (redacted !== item) changed = true;
    return redacted;
  });
  return changed ? next : value;
}

function redactRecord(value: Record<string, unknown>): Record<string, unknown> {
  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    const redacted = key === 'token' && typeof item === 'string' ? REDACTED : redactClarityTokens(item);
    if (redacted !== item) changed = true;
    next[key] = redacted;
  }
  return changed ? next : value;
}

/** Returns a copy with every string that contains the token prefix replaced; unchanged values keep their identity. */
export function redactClarityTokens<T>(value: T): T {
  if (typeof value === 'string') return (value.includes(CLARITY_TOKEN_PREFIX) ? REDACTED : value) as T;
  if (Array.isArray(value)) return redactArray(value) as T;
  if (typeof value === 'object' && value !== null) return redactRecord(value as Record<string, unknown>) as T;
  return value;
}

export function clarityActionSanitizer<A extends Action>(action: A): A {
  if (!action.type.startsWith(API_PREFIX)) return action;
  return redactClarityTokens(action);
}

export function clarityStateSanitizer<S>(state: S): S {
  if (typeof state !== 'object' || state === null) return state;
  const slice = (state as Record<string, unknown>)[MUTATIONS_REDUCER] as Record<string, unknown> | undefined;
  if (!slice || typeof slice !== 'object' || !('mutations' in slice)) return state;
  const mutations = redactClarityTokens(slice.mutations);
  if (mutations === slice.mutations) return state;
  return { ...state, [MUTATIONS_REDUCER]: { ...slice, mutations } } as S;
}
